
/* ============================== src/utils/filters.js ============================== */
// path: frontend/src/utils/filters.js
/**
 * Чистые функции применения фильтров к спискам сущностей: filterTasks и
 * filterProjects. Экспортирует функции фильтрации и CSS-хелперы состояния
 * фильтров (isFilterActive, filterSelectClass, filterDateClass).
 *
 * isMyTask / computeMyTaskProjectIds инлайн: это UX-фильтр «только мои»
 * в шапке вью, а не RBAC. Сервер уже scope-фильтрует /tasks и /projects;
 * legacy utils/permissions.js удалён (PR 2c).
 */
const isAll = (v) => v === "all" || v === "" || v == null;

const isMyTask = (task, userId) =>
  !!task && (task.assigneeId === userId || task.creatorId === userId);

const computeMyTaskProjectIds = (tasks, userId) => {
  const ids = new Set();
  if (!userId) return ids;
  for (const t of tasks) {
    if (t.archived) continue;
    if (isMyTask(t, userId)) ids.add(t.projectId);
  }
  return ids;
};

export const isFilterActive = (v) => !isAll(v);

export const filterSelectClass = (value, extra = "") =>
  "filter-select" +
  (isFilterActive(value) ? " filter-select--active" : "") +
  (extra ? ` ${extra}` : "");

export const filterDateClass = (value, focused) =>
  "filter-select filter-date" +
  (value ? "" : " filter-date--empty") +
  (focused ? " is-focused" : "") +
  (isFilterActive(value) ? " filter-date--active" : "");

export function filterTasks(tasks, filters, ctx) {
  const { employeesById, currentUserId } = ctx;
  const {
    projectId,
    taskId,
    assigneeId,
    priority,
    deptId,
    tag,
    showAll,
    deadlineBefore,
  } = filters;

  let list = tasks;

  if (!isAll(projectId)) list = list.filter((t) => t.projectId === projectId);
  if (!isAll(taskId)) list = list.filter((t) => t.id === taskId);
  if (!isAll(assigneeId))
    list = list.filter((t) => t.assigneeId === assigneeId);
  if (!isAll(priority)) list = list.filter((t) => t.priority === priority);

  if (!isAll(tag)) {
    list = list.filter((t) => Array.isArray(t.tags) && t.tags.includes(tag));
  }

  if (!isAll(deptId)) {
    list = list.filter((t) => {
      const a = t.assigneeId ? employeesById.get(t.assigneeId) : null;
      return a && a.departments.some((d) => d.deptId === deptId);
    });
  }

  if (!isAll(deadlineBefore)) {
    list = list.filter((t) => t.deadline && t.deadline <= deadlineBefore);
  }

  if (!showAll) list = list.filter((t) => isMyTask(t, currentUserId));

  return list;
}

export function filterProjects(projects, filters, ctx) {
  const { tasks, employeesById, currentUserId } = ctx;
  const {
    projectId,
    status,
    type,
    priority,
    aircraftType,
    participant,
    managerId,
    deptId,
    tag,
    showAll,
    endBefore,
  } = filters;

  let list = projects;

  if (!isAll(projectId)) list = list.filter((p) => p.id === projectId);
  if (!isAll(status)) list = list.filter((p) => p.status === status);
  if (!isAll(type)) list = list.filter((p) => (p.ptype || "prod") === type);
  if (!isAll(priority)) list = list.filter((p) => p.priority === priority);
  if (!isAll(aircraftType))
    list = list.filter((p) => p.aircraftType === aircraftType);

  if (!isAll(tag)) {
    list = list.filter((p) => Array.isArray(p.tags) && p.tags.includes(tag));
  }

  if (!isAll(endBefore)) {
    list = list.filter((p) => p.end && p.end <= endBefore);
  }

  if (!showAll) {
    const myProjectIds = computeMyTaskProjectIds(tasks, currentUserId);
    list = list.filter(
      (p) =>
        myProjectIds.has(p.id) ||
        (!!currentUserId &&
          (p.managerId === currentUserId || p.creatorId === currentUserId)),
    );
  }

  if (!isAll(managerId)) {
    list = list.filter((p) => p.managerId === managerId);
  }

  if (!isAll(participant)) {
    const ids = new Set();
    tasks.forEach((t) => {
      if (!t.archived && t.assigneeId === participant) ids.add(t.projectId);
    });
    list = list.filter((p) => ids.has(p.id));
  }

  if (!isAll(deptId)) {
    const ids = new Set();
    tasks.forEach((t) => {
      if (t.archived || !t.assigneeId) return;
      const a = employeesById.get(t.assigneeId);
      if (a && a.departments.some((d) => d.deptId === deptId)) {
        ids.add(t.projectId);
      }
    });
    list = list.filter((p) => ids.has(p.id));
  }

  return list;
}
